import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AUTH_REQUIRED_EVENT,
  authApi,
  clearStoredUser,
  getStoredUser,
  type AuthUser,
} from "@/lib/auth-api";

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isCheckingAuth: boolean;
  login: (username: string, password: string) => Promise<AuthUser>;
  loginWithGoogle: (googleIdToken: string) => Promise<AuthUser>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [isCheckingAuth, setIsCheckingAuth] = useState(() => user != null);
  const validationStarted = useRef(false);

  useEffect(() => {
    if (!user || validationStarted.current) return;

    validationStarted.current = true;
    const token = user.token;

    authApi
      .validate(token)
      .catch(() => {
        if (getStoredUser()?.token === token) {
          clearStoredUser();
          setUser(null);
        }
      })
      .finally(() => setIsCheckingAuth(false));
  }, [user]);

  useEffect(() => {
    const handleAuthRequired = () => {
      setUser(null);
      setIsCheckingAuth(false);
    };

    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const loggedUser = await authApi.login(username, password);
    setUser(loggedUser);
    setIsCheckingAuth(false);
    return loggedUser;
  }, []);

  const loginWithGoogle = useCallback(async (googleIdToken: string) => {
    const loggedUser = await authApi.loginWithGoogle(googleIdToken);
    setUser(loggedUser);
    setIsCheckingAuth(false);
    return loggedUser;
  }, []);

  const logout = useCallback(() => {
    clearStoredUser();
    setUser(null);
    setIsCheckingAuth(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user != null,
      isCheckingAuth,
      login,
      loginWithGoogle,
      logout,
    }),
    [isCheckingAuth, login, loginWithGoogle, logout, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
